import { useMemo } from 'react';
import {
  buildDimensionAnnotation,
  DIMENSION_SIDE_TEST_DISTANCE_PX,
  estimateTextHalfWidth,
  getWallMidpoint,
} from '../../../lib/floorPlanDesignerDimensions.js';
import {
  DIMENSION_ARROW_TO_TEXT_GAP_PX,
  DIMENSION_WALL_TO_ARROW_GAP_PX,
} from '../../../lib/floorPlanDesignerConfig.js';
import { boundsOverlap, oppositePlacement } from './useOverlayPlacement.js';

/** @typedef {{ x: number; y: number; width: number; height: number }} RectBounds */

/**
 * @param {{
 *   start: [number, number];
 *   end: [number, number];
 *   sideSign: 1 | -1;
 *   lengthPx: number;
 *   metersPerPixel: number;
 *   displayScale: number;
 *   text?: string;
 *   fontSizeBase?: number;
 * }} dim
 * @param {number} sideSign
 * @returns {ReturnType<typeof buildDimensionAnnotation> & { textBounds: RectBounds }}
 */
export function getDimensionScreenBounds(dim, sideSign) {
  const annotation = buildDimensionAnnotation(
    dim.start,
    dim.end,
    dim.lengthPx,
    dim.metersPerPixel,
    dim.displayScale,
    {
      sideSign,
      wallToArrowGapPx: DIMENSION_WALL_TO_ARROW_GAP_PX,
      arrowToTextGapPx: DIMENSION_ARROW_TO_TEXT_GAP_PX,
      fontSizeBase: dim.fontSizeBase,
    },
  );
  const labelText =
    dim.text ??
    `${Math.max(0, dim.lengthPx * dim.metersPerPixel).toFixed(2)} m`;
  const halfW = estimateTextHalfWidth(labelText, annotation.fontSize);
  const halfH = annotation.fontSize / 2;
  const textBounds = {
    x: annotation.textX - halfW,
    y: annotation.textY - halfH,
    width: halfW * 2,
    height: halfH * 2,
  };
  return { ...annotation, textBounds };
}

/**
 * @param {RectBounds} bounds
 * @param {RectBounds[]} obstacles
 * @returns {boolean}
 */
function boundsCollidesWithObstacles(bounds, obstacles) {
  return obstacles.some((obstacle) => boundsOverlap(bounds, obstacle));
}

/**
 * @param {{
 *   start: [number, number];
 *   end: [number, number];
 *   sideSign: 1 | -1;
 *   placementKind?: 'exterior' | 'interior';
 *   lengthPx: number;
 *   metersPerPixel: number;
 *   displayScale: number;
 *   text?: string;
 *   fontSizeBase?: number;
 *   obstacles?: RectBounds[];
 *   actionMenuBounds?: { x: number; y: number; width: number; height: number; placement?: string } | null;
 *   viewportTransform?: { offsetX: number; offsetY: number; scale: number };
 * }} params
 * @returns {{ sideSign: 1 | -1; annotation: ReturnType<typeof buildDimensionAnnotation> }}
 */
export function resolveDimensionPlacement({
  start,
  end,
  sideSign,
  placementKind = 'exterior',
  lengthPx,
  metersPerPixel,
  displayScale,
  text,
  fontSizeBase,
  obstacles = [],
  actionMenuBounds = null,
  viewportTransform = null,
}) {
  const dim = {
    start,
    end,
    sideSign,
    lengthPx,
    metersPerPixel,
    displayScale,
    text,
    fontSizeBase,
  };

  const preferred = getDimensionScreenBounds(dim, sideSign);
  const oppositeSign = /** @type {1 | -1} */ (sideSign === 1 ? -1 : 1);
  const alternate = getDimensionScreenBounds(dim, oppositeSign);

  const toScreenBounds = (annotation) => {
    if (!viewportTransform) return annotation.textBounds;
    const scale = viewportTransform.scale;
    return {
      x: annotation.textBounds.x * scale + viewportTransform.offsetX,
      y: annotation.textBounds.y * scale + viewportTransform.offsetY,
      width: annotation.textBounds.width * scale,
      height: annotation.textBounds.height * scale,
    };
  };

  const screenObstacles = [...obstacles];
  if (actionMenuBounds) {
    screenObstacles.push(actionMenuBounds);
  }

  const preferredScreen = toScreenBounds(preferred);
  const alternateScreen = toScreenBounds(alternate);

  const preferredCollides = boundsCollidesWithObstacles(preferredScreen, screenObstacles);
  const alternateCollides = boundsCollidesWithObstacles(alternateScreen, screenObstacles);

  if (!preferredCollides) {
    return { sideSign, annotation: preferred };
  }

  if (!alternateCollides) {
    const flippedKindMatches =
      (placementKind === 'exterior' && oppositeSign !== sideSign) ||
      (placementKind === 'interior' && oppositeSign !== sideSign);
    if (flippedKindMatches || placementKind === 'interior') {
      return { sideSign: oppositeSign, annotation: alternate };
    }
  }

  if (actionMenuBounds?.placement && viewportTransform) {
    const preferredSide = oppositePlacement(
      /** @type {'top' | 'bottom' | 'left' | 'right'} */ (actionMenuBounds.placement),
    );
    const midPreferred = getWallMidpoint(start, end);
    const midScreen = {
      x: midPreferred[0] * viewportTransform.scale + viewportTransform.offsetX,
      y: midPreferred[1] * viewportTransform.scale + viewportTransform.offsetY,
    };
    const menuCenter = {
      x: actionMenuBounds.x + actionMenuBounds.width / 2,
      y: actionMenuBounds.y + actionMenuBounds.height / 2,
    };
    if (preferredSide === 'top' || preferredSide === 'bottom') {
      const pickNegative = midScreen.y > menuCenter.y;
      const pickedSign = pickNegative ? -1 : 1;
      return {
        sideSign: /** @type {1 | -1} */ (pickedSign),
        annotation: getDimensionScreenBounds(dim, /** @type {1 | -1} */ (pickedSign)),
      };
    }
  }

  if (!alternateCollides) {
    return { sideSign: oppositeSign, annotation: alternate };
  }

  const distToObstacles = (bounds) => {
    const center = {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2,
    };
    let minDist = Infinity;
    screenObstacles.forEach((obstacle) => {
      const obstacleCenter = {
        x: obstacle.x + obstacle.width / 2,
        y: obstacle.y + obstacle.height / 2,
      };
      minDist = Math.min(
        minDist,
        Math.hypot(center.x - obstacleCenter.x, center.y - obstacleCenter.y),
      );
    });
    return minDist;
  };

  const preferredDist = distToObstacles(preferredScreen);
  const alternateDist = distToObstacles(alternateScreen);
  if (alternateDist > preferredDist) {
    return { sideSign: oppositeSign, annotation: alternate };
  }

  return { sideSign, annotation: preferred };
}

/**
 * @param {ReturnType<typeof buildDimensionAnnotation>} annotation
 * @param {{ offsetX: number; offsetY: number; scale: number }} viewportTransform
 * @returns {RectBounds}
 */
export function annotationToScreenTextBounds(annotation, viewportTransform) {
  const halfW = annotation.textOffsetX;
  const halfH = annotation.textOffsetY;
  return {
    x: annotation.textX * viewportTransform.scale + viewportTransform.offsetX - halfW * viewportTransform.scale,
    y: annotation.textY * viewportTransform.scale + viewportTransform.offsetY - halfH * viewportTransform.scale,
    width: halfW * 2 * viewportTransform.scale,
    height: halfH * 2 * viewportTransform.scale,
  };
}

/**
 * @param {ReturnType<typeof buildDimensionAnnotation>} annotation
 * @param {{ offsetX: number; offsetY: number; scale: number }} viewportTransform
 * @param {number} [padding]
 * @returns {RectBounds}
 */
export function inflateDimensionTextBounds(annotation, viewportTransform, padding = 4) {
  const bounds = annotationToScreenTextBounds(annotation, viewportTransform);
  return {
    x: bounds.x - padding,
    y: bounds.y - padding,
    width: bounds.width + padding * 2,
    height: bounds.height + padding * 2,
  };
}

/**
 * @param {{
 *   start: [number, number];
 *   end: [number, number];
 *   displayScale: number;
 *   sideSign?: 1 | -1;
 *   actionMenuBounds?: { x: number; y: number; width: number; height: number; placement?: string } | null;
 *   viewportTransform?: { offsetX: number; offsetY: number; scale: number };
 * }} params
 * @returns {number}
 * @deprecated Prefer resolveDimensionPlacement with full dim metadata
 */
export function resolveWallDimensionLineOffset({
  start,
  end,
  displayScale,
  sideSign = 1,
  actionMenuBounds = null,
  viewportTransform = null,
}) {
  const safeScale = Math.max(displayScale || 1, 0.0001);
  const magnitude = DIMENSION_WALL_TO_ARROW_GAP_PX / safeScale;
  const resolved = resolveDimensionPlacement({
    start,
    end,
    sideSign,
    placementKind: 'exterior',
    lengthPx: Math.hypot(end[0] - start[0], end[1] - start[1]),
    metersPerPixel: 0.01,
    displayScale,
    actionMenuBounds,
    viewportTransform,
  });
  return magnitude * (resolved.sideSign < 0 ? -1 : 1);
}

/**
 * @param {Parameters<typeof resolveDimensionPlacement>[0]} params
 */
export function useDimensionPlacement(params) {
  return useMemo(
    () => resolveDimensionPlacement(params),
    [
      params.start,
      params.end,
      params.sideSign,
      params.placementKind,
      params.lengthPx,
      params.metersPerPixel,
      params.displayScale,
      params.text,
      params.fontSizeBase,
      params.obstacles,
      params.actionMenuBounds,
      params.viewportTransform,
    ],
  );
}

export { DIMENSION_SIDE_TEST_DISTANCE_PX };
